import { makeStyles, tokens, Spinner, Badge } from '@fluentui/react-components';
import {
    CheckmarkCircle24Filled,
    Warning24Regular,
    Dismiss24Filled,
    PlugConnected24Regular,
    Stack24Regular,
    CalendarLtr24Regular,
    Open16Regular,
} from '@fluentui/react-icons';
import type { SearchIndexerContextValue } from '../../hooks/useSearchIndexer';
import { SEARCH_INDEX_STALE_DAYS } from '../../config/constants';

const RING_SIZE = 168;
const RING_STROKE = 14;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const useStyles = makeStyles({
    card: {
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    },
    body: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    ringPanel: {
        flex: '0 0 300px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
        padding: '40px 32px',
        backgroundColor: tokens.colorNeutralBackground2,
    },
    ringWrap: {
        position: 'relative',
        width: `${RING_SIZE}px`,
        height: `${RING_SIZE}px`,
    },
    ringSvg: {
        transform: 'rotate(-90deg)',
    },
    ringTrack: {
        transition: 'stroke 0.2s ease',
    },
    ringFill: {
        transition: 'stroke-dashoffset 0.5s ease, stroke 0.2s ease',
    },
    ringFillLoading: {
        transformOrigin: '50% 50%',
        animationName: {
            from: { transform: 'rotate(0deg)' },
            to: { transform: 'rotate(360deg)' },
        },
        animationDuration: '1.4s',
        animationTimingFunction: 'linear',
        animationIterationCount: 'infinite',
    },
    ringCenter: {
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '4px',
    },
    ringCenterText: {
        fontSize: tokens.fontSizeBase400,
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
    },
    ringCaption: {
        textAlign: 'center',
    },
    ringCaptionLabel: {
        display: 'block',
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
    },
    ringCaptionValue: {
        display: 'block',
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
    },
    detailList: {
        flex: '1 1 320px',
        display: 'flex',
        flexDirection: 'column',
    },
    detailRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        padding: '18px 24px',
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    detailRowLast: {
        borderBottom: 'none',
    },
    detailIcon: {
        flexShrink: 0,
        width: '40px',
        height: '40px',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    detailText: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        minWidth: 0,
    },
    detailLabel: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
    },
    detailValue: {
        fontSize: tokens.fontSizeBase400,
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
    },
    detailRight: {
        marginLeft: 'auto',
        flexShrink: 0,
        fontSize: tokens.fontSizeBase400,
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground2,
    },
    workerRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '12px 24px',
        backgroundColor: tokens.colorNeutralBackground2,
        borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground2,
    },
});

interface Props {
    ctx: SearchIndexerContextValue;
    staleDays?: number;
}

export function IndexerStatusCard({ ctx, staleDays = SEARCH_INDEX_STALE_DAYS }: Props) {
    const styles = useStyles();
    const {
        registryExists,
        registryUrl,
        sourcesLoading,
        sources,
        workerReady,
        workerLoading,
        workerStatus,
        workerError,
    } = ctx;

    const oldestSyncDate = sources.reduce<string | null>((oldest, s) => {
        if (!s.lastFullSyncDate) return oldest;
        if (!oldest || s.lastFullSyncDate < oldest) return s.lastFullSyncDate;
        return oldest;
    }, null);

    const isStale = oldestSyncDate
        ? (Date.now() - new Date(oldestSyncDate).getTime()) / 86_400_000 > staleDays
        : sources.length > 0;

    const formatDate = (iso: string | null) => {
        if (!iso) return 'Never';
        const d = new Date(iso);
        return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
    };

    const relativeSync = (iso: string | null) => {
        if (!iso) return '';
        const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
        if (days <= 0) return 'Today';
        if (days === 1) return 'Yesterday';
        return `${days} days ago`;
    };

    // Engine health drives the ring: green + full = ready, gold + spinning = loading,
    // red + full = error. Percentage is a status indicator here, not a literal ratio.
    const engineState: 'ready' | 'loading' | 'error' = workerError
        ? 'error'
        : workerReady
        ? 'ready'
        : 'loading';

    // The ring itself always wears the brand accent (gold) — only the center icon
    // and badges carry the actual health color, so "healthy" reads as gold ring +
    // green check, never a ring that turns fully green.
    const ringColor = {
        ready: tokens.colorPaletteMarigoldForeground1,
        loading: tokens.colorPaletteMarigoldForeground1,
        error: tokens.colorPaletteRedForeground1,
    }[engineState];

    // Ready/error are both "full" states — the track sits behind a ~97% arc, so
    // it should match the panel background (a clean background notch at the
    // gap) rather than a same-hue tint that barely reads as distinct from the
    // fill. Loading is genuinely partial, so its track stays a visible tinted
    // remaining-arc.
    const ringTrackColor = {
        ready: tokens.colorNeutralBackground2,
        loading: tokens.colorPaletteMarigoldBackground2,
        error: tokens.colorNeutralBackground2,
    }[engineState];

    const centerIconColor = {
        ready: tokens.colorPaletteGreenForeground1,
        loading: tokens.colorPaletteMarigoldForeground1,
        error: tokens.colorPaletteRedForeground1,
    }[engineState];

    const ringPct = engineState === 'loading' ? 65 : 97; // small gap even when "full" — matches ring gauge convention
    const ringOffset = RING_CIRCUMFERENCE * (1 - ringPct / 100);

    const registryTone = registryExists ? 'green' : registryExists === false ? 'marigold' : 'neutral';
    const registryTint = {
        green: { bg: tokens.colorPaletteGreenBackground1, fg: tokens.colorPaletteGreenForeground1 },
        marigold: { bg: tokens.colorPaletteMarigoldBackground1, fg: tokens.colorPaletteMarigoldForeground1 },
        neutral: { bg: tokens.colorNeutralBackground3, fg: tokens.colorNeutralForeground3 },
    }[registryTone];

    // Blue by default (its own accent, distinct from the green Registry row and
    // tan Sources row) — overridden to red only when genuinely stale, as a warning.
    const lastIndexedTint = isStale
        ? { bg: tokens.colorPaletteRedBackground1, fg: tokens.colorPaletteRedForeground1 }
        : { bg: '#eaf3fb', fg: tokens.colorPaletteBlueForeground2 };

    return (
        <div className={styles.card}>
            <div className={styles.body}>
                <div className={styles.ringPanel}>
                    <div className={styles.ringWrap}>
                        <svg
                            className={styles.ringSvg}
                            width={RING_SIZE}
                            height={RING_SIZE}
                            viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
                        >
                            <circle
                                className={styles.ringTrack}
                                cx={RING_SIZE / 2}
                                cy={RING_SIZE / 2}
                                r={RING_RADIUS}
                                fill="none"
                                stroke={ringTrackColor}
                                strokeWidth={RING_STROKE}
                            />
                            <circle
                                className={`${styles.ringFill} ${
                                    engineState === 'loading' ? styles.ringFillLoading : ''
                                }`}
                                cx={RING_SIZE / 2}
                                cy={RING_SIZE / 2}
                                r={RING_RADIUS}
                                fill="none"
                                stroke={ringColor}
                                strokeWidth={RING_STROKE}
                                strokeLinecap="round"
                                strokeDasharray={RING_CIRCUMFERENCE}
                                strokeDashoffset={ringOffset}
                            />
                        </svg>
                        <div className={styles.ringCenter}>
                            {engineState === 'ready' && (
                                <CheckmarkCircle24Filled
                                    style={{ color: centerIconColor, fontSize: '28px' }}
                                />
                            )}
                            {engineState === 'loading' && <Spinner size="small" />}
                            {engineState === 'error' && (
                                <Dismiss24Filled style={{ color: centerIconColor, fontSize: '28px' }} />
                            )}
                            <span className={styles.ringCenterText}>
                                {engineState === 'ready' && 'Ready'}
                                {engineState === 'loading' && 'Loading'}
                                {engineState === 'error' && 'Error'}
                            </span>
                        </div>
                    </div>
                    <div className={styles.ringCaption}>
                        <span className={styles.ringCaptionLabel}>Indexer engine</span>
                        <span className={styles.ringCaptionValue}>
                            {engineState === 'ready' && 'Operational'}
                            {engineState === 'loading' && 'Starting…'}
                            {engineState === 'error' && 'Unavailable'}
                        </span>
                    </div>
                </div>

                <div className={styles.detailList}>
                    <div className={styles.detailRow}>
                        <div className={styles.detailIcon} style={{ backgroundColor: registryTint.bg }}>
                            <PlugConnected24Regular style={{ color: registryTint.fg }} />
                        </div>
                        <div className={styles.detailText}>
                            <span className={styles.detailLabel}>Registry</span>
                            <span className={styles.detailValue} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {sourcesLoading
                                    ? 'Checking…'
                                    : registryExists === null
                                    ? 'Unknown'
                                    : registryExists
                                    ? 'Connected'
                                    : 'Not configured'}
                                {registryExists && registryUrl && (
                                    <a
                                        href={registryUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title="Open InjioSearchRegistry list in SharePoint"
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '3px',
                                            fontSize: tokens.fontSizeBase200,
                                            fontWeight: tokens.fontWeightRegular,
                                            color: tokens.colorBrandForeground1,
                                            textDecoration: 'none',
                                        }}
                                    >
                                        Open in SharePoint
                                        <Open16Regular style={{ width: 12, height: 12 }} />
                                    </a>
                                )}
                            </span>
                        </div>
                        {!sourcesLoading && registryExists !== null && (
                            <div className={styles.detailRight}>
                                <Badge color={registryExists ? 'success' : 'warning'} appearance="tint">
                                    {registryExists ? 'Healthy' : 'Action needed'}
                                </Badge>
                            </div>
                        )}
                    </div>

                    <div className={styles.detailRow}>
                        <div
                            className={styles.detailIcon}
                            style={{ backgroundColor: tokens.colorPaletteMarigoldBackground1 }}
                        >
                            <Stack24Regular style={{ color: tokens.colorPaletteMarigoldForeground1 }} />
                        </div>
                        <div className={styles.detailText}>
                            <span className={styles.detailLabel}>Connected sources</span>
                            <span className={styles.detailValue}>
                                {sourcesLoading ? '…' : `${sources.length} source${sources.length === 1 ? '' : 's'}`}
                            </span>
                        </div>
                        <div className={styles.detailRight}>{sourcesLoading ? '…' : sources.length}</div>
                    </div>

                    <div className={`${styles.detailRow} ${styles.detailRowLast}`}>
                        <div className={styles.detailIcon} style={{ backgroundColor: lastIndexedTint.bg }}>
                            <CalendarLtr24Regular style={{ color: lastIndexedTint.fg }} />
                        </div>
                        <div className={styles.detailText}>
                            <span className={styles.detailLabel}>Last indexed</span>
                            <span className={styles.detailValue}>{formatDate(oldestSyncDate)}</span>
                        </div>
                        {oldestSyncDate && (
                            <div className={styles.detailRight}>
                                {isStale ? (
                                    <Badge color="danger" icon={<Warning24Regular />} appearance="tint">
                                        {relativeSync(oldestSyncDate)}
                                    </Badge>
                                ) : (
                                    relativeSync(oldestSyncDate)
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {(workerLoading || workerError || workerStatus) && (
                <div className={styles.workerRow}>
                    {workerLoading && !workerError && <Spinner size="extra-tiny" />}
                    {workerError ? (
                        <span style={{ color: tokens.colorPaletteRedForeground1 }}>{workerError}</span>
                    ) : (
                        <span>{workerStatus}</span>
                    )}
                </div>
            )}
        </div>
    );
}
